import PropTypes from 'prop-types'
import classnames from 'classnames'
import {Map, List} from 'immutable'
import {useCallback} from 'react'
import Highlighter from 'react-highlight-words'

import ResponseOwnership from './ResponseOwnership'

import {firstGroupId} from '../../../../../../lib/responsesHelper'
import {categorySlugByItem} from '../../../../../../lib/plan_data/category'
import {viewItemPath} from '../../../../../../lib/urlTools'
import routerUtils from '../../../../../../lib/routerUtils'
import {Text} from '../../../blocks/Texts'
import {contactLabel} from '../../../../../../lib/contactTools'
import {dateResponseValue} from '../../../../../../lib/dateTools'
import {formattedResponseValue} from '../../../../../../lib/plan_data/itemResponsesHelper'
import {lookUpSearchDictionary} from '../searchDictionary'

import './response.scss'

// Highlighter throws on anything that isn't a string, so guard against
// truthy non-string values too (e.g. an unexpected Immutable Map). - Kay
const highlightableText = value =>
  (typeof value === 'string' || typeof value === 'number' ? String(value) : '')

const Response = ({
  categories,
  currentUserFirstLetter,
  isHousehold,
  ownersDictionary,
  responseResultGroup,
  logEvent,
  textValue,
  primitivesTopKitElementsMap
}) => {
  // Get the first response with response data, allowing falsy data like 0 or ''.
  const response = responseResultGroup.find(resp => resp.getIn(['value', 'data']) != null, null, Map())
  // Bail if there is no response with data in the response group.
  // Some legacy contact responses do not have values on prod. - Kay
  if (response.isEmpty()) return null

  const itemResponse = response.get('itemResponse')
  const hasFileResponse = responseResultGroup.some(resp => resp.get('element-type') === 'FILE')

  const url = () => viewItemPath({
    elementId: primitivesTopKitElementsMap.getIn([
      response.get('element-id'),
      'id'
    ]),
    groupId: firstGroupId(responseResultGroup),
    itemSlug: itemResponse.get('slug'),
    slug: categorySlugByItem({item: itemResponse, categories}),
    path: '/cm/category/'
  })

  const onClick = useCallback(() => {
    logEvent('clicked_search_result', {
      type: 'response',
      item: itemResponse.get('header'),
      wildcard: itemResponse.get('header')
    })
    routerUtils.push(url())
  }, [])

  // Always returns a string, guarding against legacy or malformed response data,
  // because Highlighter throws on anything that isn't a string. - Kay
  const responseView = () => {
    const responseValue = response.getIn(['value', 'data'])
    const elementType = response.get('element-type')

    if (elementType === 'CONTACT')
      return Map.isMap(responseValue) ? contactLabel(responseValue) : ''
    else if (elementType === 'FILE')
      return List.isList(responseValue) ? responseValue.map(file => (Map.isMap(file) ? file.get('name') : null)).filter(name => name).join(', ') : ''
    else if (elementType === 'ADDRESS')
      return Map.isMap(responseValue) ? responseValue.valueSeq().filter(value => value).join(', ') : ''
    else if (elementType === 'DATE')
      return Map.isMap(responseValue) ? dateResponseValue(responseValue) : ''

    if (List.isList(responseValue)) return formattedResponseValue({elementType, responseValue}).join(', ')
    else if (typeof responseValue === 'number') return String(responseValue)
    else return typeof responseValue === 'string' ? responseValue : ''
  }

  const textArray = textValue ? lookUpSearchDictionary(textValue.trim()).toJS() : []

  return (
    <div
      className='flex-container result border-bottom response-result'
      onClick={onClick}>
      <div className='profile-circle'>{currentUserFirstLetter}</div>
      <div className='result-with-ownership'>
        <div className='result-content'>
          <Text className='bold-text'>
            <Highlighter
              searchWords={textArray}
              textToHighlight={highlightableText(itemResponse.get('header'))}
              autoEscape={true}
            />
          </Text>
          <Text
            className={classnames('keyfield link-text', {
              'has-file-response': hasFileResponse
            })}>
            {response.get('key-field')}
          </Text>
          <div className='flex-container'>
            <Text className='display-name'>
              <Highlighter
                highlightClassName='bold-text'
                searchWords={textArray}
                textToHighlight={highlightableText(response.get('display-name'))}
                autoEscape={true}
              />
              :
            </Text>
            <Text className='response-value'>
              <Highlighter
                highlightClassName='bold-text'
                searchWords={textArray}
                textToHighlight={responseView()}
                autoEscape={true}
              />
            </Text>
          </div>
        </div>
        {isHousehold && (
          <ResponseOwnership
            responseOwnership={response.get('ownership-ids') || List()}
            ownersDictionary={ownersDictionary}
          />
        )}
      </div>
    </div>
  )
}

Response.propTypes = {
  categories: PropTypes.instanceOf(List),
  currentUserFirstLetter: PropTypes.string,
  isHousehold: PropTypes.bool,
  items: PropTypes.instanceOf(List),
  logEvent: PropTypes.func,
  ownersDictionary: PropTypes.instanceOf(Map),
  responseResultGroup: PropTypes.instanceOf(List),
  textValue: PropTypes.string,
  primitivesTopKitElementsMap: PropTypes.instanceOf(Map)
}

export default Response
